import React, { useEffect } from 'react'
import { Gem, Star, Loader, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import confetti from 'canvas-confetti'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import usePaymentStore from '../store/usePaymentStore'
import useAppStore from '../../../store/useAppStore'
import useAppEnvironment from '../../../hooks/useAppEnvironment'
import useAudioManager from '../../../hooks/useAudioManager'
import { PAYMENT_METHODS } from '../services/paymentApi'

/**
 * TelegramStarsPackages Component
 * Компонент для отображения и покупки пакетов через Telegram Stars
 */
const TelegramStarsPackages = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isTelegramMiniApp } = useAppEnvironment()
  const { loadProfile } = useAppStore()
  const { playSound } = useAudioManager({
    success: 'successPayment'
  })
  const {
    packages,
    isLoadingPackages,
    packagesError,
    isProcessingPayment,
    processingPackageId,
    loadPackages,
    createPayment
  } = usePaymentStore()

  // Загружаем пакеты при монтировании
  useEffect(() => {
    loadPackages(PAYMENT_METHODS.TELEGRAM_STARS)
  }, [loadPackages])

  // Обработка выбора пакета
  const handlePackageSelect = async (pkg) => {
    if (isProcessingPayment) return

    if (!isTelegramMiniApp) {
      const botWindow = window.open('https://t.me/SkinozBot', '_blank')

      if (botWindow) {
        try {
          botWindow.opener = null
        } catch (error) {
          console.warn('⚠️ Не удалось очистить opener у окна Telegram бота:', error)
        }
      } else {
        window.location.assign('https://t.me/SkinozBot')
      }

      return
    }

    // Callback при успешной оплате
    const onSuccess = async () => {
      playSound('success')
      // Отправляем цель в Яндекс.Метрику
      if (window.ym) {
        window.ym(104258304, 'reachGoal', 'deposit-stars')
      }
      
      // Запускаем конфетти
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00FF7F', '#4EFFC6', '#FFD700']
      })
      
      // Обновляем баланс
      await loadProfile()
      toast.success(t('payment.paymentSuccess'))
      
      // Перенаправляем на главную
      setTimeout(() => {
        navigate('/')
      }, 1500)
    }

    // Callback при ошибке
    const onError = (error) => {
      toast.error(error || t('payment.failed'))
    }

    // Создаем платеж
    await createPayment(pkg, onSuccess, onError)
  }

  // Состояние загрузки
  if (isLoadingPackages) {
    return (
      <div className="grid grid-cols-1 gap-4 max-w-md mx-auto">
        {[...Array(7)].map((_, index) => (
          <div key={index} className="bg-surface rounded-lg border border-white border-opacity-10 p-4 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-600 rounded-lg"></div>
                <div>
                  <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                  <div className="h-4 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
              <div className="text-right">
                <div className="h-5 bg-gray-600 rounded w-12 mb-2"></div>
                <div className="h-6 bg-gray-600 rounded w-16"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Состояние ошибки
  if (packagesError && !isLoadingPackages) {
    return (
      <div className="text-center py-8">
        <XCircle size={48} className="text-red-400 mx-auto mb-4" />
        <p className="text-red-400 mb-4">{packagesError}</p>
        <button
          onClick={() => loadPackages(PAYMENT_METHODS.TELEGRAM_STARS)}
          className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
        >
          {t('common.retry')}
        </button>
      </div>
    )
  }

  // Список пакетов
  return (
    <div className="grid grid-cols-1 gap-4 max-w-md mx-auto">
      {packages.map((pkg) => (
        <div
          key={pkg.id}
          className={`relative bg-surface rounded-lg border border-white border-opacity-10 p-4 cursor-pointer transition-all hover:border-accent-primary/50 hover:scale-[1.02] active:scale-[0.98] ${
            pkg.popular ? 'ring-2 ring-accent-primary ring-opacity-50' : ''
          } ${
            isProcessingPayment && processingPackageId === pkg.id 
              ? 'opacity-50 cursor-not-allowed' 
              : ''
          }`}
          onClick={() => !isProcessingPayment && handlePackageSelect(pkg)}
        >
          {/* Бейдж "Популярно" */}
          {pkg.popular && (
            <div className="absolute -top-2 -right-2">
              <span className="inline-block px-3 py-1 bg-accent-primary text-dark-primary text-xs font-bold rounded-full shadow-lg">
                {t('payment.popular')}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            {/* Левая часть - иконка и количество кристаллов */}
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                <Gem size={28} className="text-dark-primary" />
              </div>
              <div>
                <p className="text-white font-bold text-xl">
                  {pkg.crystals.toLocaleString()}
                </p>
                <p className="text-text-secondary text-sm">
                  {t('payment.crystals')}
                </p>
              </div>
            </div>
            
            {/* Правая часть - цена в Stars или индикатор загрузки */}
            <div className="text-right">
              {isProcessingPayment && processingPackageId === pkg.id ? (
                <div className="flex items-center gap-2 text-accent-primary">
                  <Loader size={20} className="animate-spin" />
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-yellow-400 font-bold text-lg">
                  <Star size={18} fill="currentColor" />
                  <span>{pkg.stars}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default TelegramStarsPackages
